<template>
  <div
    v-loading.fullscreen.lock="isUploading"
    :element-loading-text="loadingText"
    class="asset-inline-upload"
  >
    <el-upload
      class="upload-area"
      drag
      :auto-upload="false"
      :show-file-list="false"
      :accept="acceptedFileTypes"
      @change="handleFileChange"
    >
      <el-icon class="el-icon--upload">
        <font-awesome-icon :icon="['fas', 'cloud-upload']" />
      </el-icon>
      <div class="el-upload__text">
        Drop file here or <em>click to upload</em>
      </div>
      <template #tip>
        <div class="el-upload__tip">Accepts: {{ acceptTypeDisplay }}</div>
      </template>
    </el-upload>
  </div>
</template>
<script>
import { UploadFilled } from '@element-plus/icons-vue';
import {
  ASSET_CONFIG,
  uploadAsset,
  getAcceptedFileTypes,
} from 'src/util/assetUtils.js';

export default {
  name: 'AssetInlineUpload',

  components: {
    UploadFilled,
  },

  props: {
    assetTypes: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every((v) => ASSET_CONFIG[v] != undefined);
      },
    },
    // Allows further refinement of the Types, but types must be set first
    allowedExtensions: {
      type: Array,
      validator: (value, props) => {
        let extensionSet = new Set();
        props.assetTypes.every((a) => Object.entries(ASSET_CONFIG[a].extensions).forEach((e) => extensionSet.add(e[0])));
        return props.allowedExtensions.every((e) => extensionSet.has(e));
      },
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    isVerification: {
      type: Boolean,
      default: false,
    },
    isUserPhoto: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['upload-complete', 'upload-error'],

  data() {
    return {
      isUploading: false,
      currentUploadCount: 0,
      totalUploadCount: 0,
    };
  },

  computed: {
    loadingText() {
      return `Uploading files... (${this.currentUploadCount} of ${this.totalUploadCount})`;
    },
    acceptedFileTypes() {
      return getAcceptedFileTypes(this.assetTypes, this.allowedExtensions);
    },
    acceptTypeDisplay() {
      return this.assetTypes.join(', ');
    },
  },

  methods: {
    async handleFileChange(file) {
      if (!file.raw) {
        this.$message({
          message: 'Error procesing file upload. Please try again',
          type: 'error',
          showClose: true,
        })
        return false;
      }
      if (!this.acceptedFileTypes.split(',').includes(file.raw.type)) {
        this.$message({
          message: `Please provide files of the following types: ${this.allowedExtensions}`,
          type: 'error',
          showClose: true,
        });
        return false;
      }
      await this.uploadFiles([file.raw]);
    },

    async uploadFiles(files) {
      this.isUploading = true;
      this.totalUploadCount = files.length;
      this.currentUploadCount = 0;

      for (const file of files) {
        this.currentUploadCount++;
        // Set isPublic/isVerification property on file
        file.isPublic = this.isPublic;
        file.isVerification = this.isVerification;
        file.isUserPhoto = this.isUserPhoto;
        const { success, asset, error } = await uploadAsset(file);

        if (success) {
          this.$emit('upload-complete', asset);
        } else {
          this.$emit('upload-error', error);
        }
      }

      this.isUploading = false;
      this.currentUploadCount = 0;
      this.totalUploadCount = 0;
    },
  },
};
</script>

<style scoped>
.asset-inline-upload {
  width: 100%;
}

.upload-area {
  width: 100%;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: auto;
  padding: 20px;
}

.el-upload__text {
  margin: 10px 0;

  em {
    color: #409eff;
    font-style: normal;
  }
}

.el-upload__tip {
  font-size: 12px;
  color: #909399;
  margin-top: 7px;
}
</style>
